/* ============================================================
   234LABS — newsletter-popup.js
   Delay / exit-intent trigger for the homepage newsletter <dialog>.
   The form itself is a plain (non-AJAX) Shopify customer form, so signup
   works even with this script blocked — it just won't auto-pop.

   Theme-editor note: every settings tweak makes Shopify swap this section's
   HTML for a fresh copy — a brand new <dialog> element each time — but the
   <script> tag's URL doesn't change, so the browser only ever runs this
   module's top-level code ONCE for the whole page. A page-level "ran
   already" guard would therefore silently stop working after the first
   edit. So init is per-<dialog>-element (guarded on the element itself,
   not on window), and re-runs on Shopify's `shopify:section:load` /
   `shopify:section:select` editor events, which keep firing every time.
   ============================================================ */
(function () {
  const KEY_DISMISS = '234:newsletter:dismissed';
  const KEY_SUBSCRIBED = '234:newsletter:subscribed';

  function initPopup(dlg) {
    if (!dlg || dlg.__234init || typeof dlg.showModal !== 'function') return;
    dlg.__234init = true;

    const inEditor = !!(window.Shopify && window.Shopify.designMode);
    // The Black Friday takeover remembers its own dismissal, so someone who closed the
    // everyday popup earlier still sees the sale announcement once.
    const scope = dlg.dataset.bf ? ':bf26' : '';
    const KEY_DISMISS_S = KEY_DISMISS + scope;
    const KEY_SUBSCRIBED_S = KEY_SUBSCRIBED + scope;
    const hideDays = Number(dlg.dataset.hideDays || 7);
    const showOnMobile = dlg.dataset.mobile !== 'false';
    const exitIntent = dlg.dataset.exitIntent === 'true';
    const delay = Number(dlg.dataset.delay || 6000);
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    // Rendered only inside the {% if form.posted_successfully? %} branch —
    // its presence *is* the signal, no Liquid-side flag needed.
    const justSubscribed = !!dlg.querySelector('.newsletter-popup__success');

    const suppressed = () => {
      try {
        if (localStorage.getItem(KEY_SUBSCRIBED_S) === '1') return true;
        const t = Number(localStorage.getItem(KEY_DISMISS_S) || 0);
        return t > 0 && Date.now() - t < hideDays * 86400000;
      } catch (e) {
        return false;
      }
    };
    const suppress = () => {
      if (inEditor) return; // don't let editor testing pollute real suppression state
      try {
        localStorage.setItem(KEY_DISMISS_S, String(Date.now()));
      } catch (e) {}
    };
    const markSubscribed = () => {
      if (inEditor) return;
      try {
        localStorage.setItem(KEY_SUBSCRIBED_S, '1');
      } catch (e) {}
    };

    const open = () => {
      if (!dlg.open) {
        try {
          dlg.showModal();
        } catch (e) {}
      }
    };
    const close = () => {
      if (dlg.open) dlg.close();
    };

    // Black Friday countdown: Days / Hours / Minutes / Seconds, ticking every second.
    // In a theme preview with a "preview as if it were" time it runs from that time.
    const clock = dlg.querySelector('[data-pop-count]');
    if (clock) {
      const to = Number(clock.dataset.to);
      const rendered = Number(clock.dataset.now);
      const preview = document.documentElement.hasAttribute('data-bf-preview');
      const offset = preview && rendered ? rendered - Date.now() / 1000 : 0;
      const cells = {};
      clock.querySelectorAll('[data-u]').forEach((el) => (cells[el.dataset.u] = el));
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = Math.max(0, Math.floor(to - (Date.now() / 1000 + offset)));
        const v = {
          d: Math.floor(left / 86400),
          h: Math.floor((left % 86400) / 3600),
          m: Math.floor((left % 3600) / 60),
          s: left % 60,
        };
        Object.keys(cells).forEach((k) => {
          const t = pad(v[k]);
          if (cells[k].textContent !== t) cells[k].textContent = t;
        });
      };
      tick();
      window.setInterval(tick, 1000);
    }

    dlg.addEventListener('click', (e) => {
      if (e.target.closest('[data-newsletter-popup-close]') || e.target === dlg) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && dlg.open) close();
    });
    // Fires on any dismissal path (X, backdrop, Esc) — not on the real page
    // navigation the signup form does, so this only ever means "closed
    // without subscribing".
    dlg.addEventListener('close', suppress);

    if (inEditor) {
      // Always visible immediately while you're customizing it — no delay,
      // no exit-intent-only, no localStorage suppression getting in the way.
      open();
      return;
    }

    if (justSubscribed) {
      markSubscribed();
      open();
    } else if ((showOnMobile || !isMobile) && !suppressed()) {
      let timer = window.setTimeout(open, delay);

      if (exitIntent && !isMobile) {
        const onLeave = (e) => {
          if (e.clientY <= 0 && !dlg.open) {
            window.clearTimeout(timer);
            open();
            document.removeEventListener('mouseout', onLeave);
          }
        };
        document.addEventListener('mouseout', onLeave);
      }
    }
  }

  initPopup(document.querySelector('[data-newsletter-popup]'));

  // Theme editor: re-bind to the fresh <dialog> every time this section
  // reloads (any settings change), and pop it open when the merchant
  // selects the section in the sidebar so it's actually visible to edit.
  document.addEventListener('shopify:section:load', (e) => {
    const dlg = e.target.querySelector && e.target.querySelector('[data-newsletter-popup]');
    if (dlg) initPopup(dlg);
  });
  document.addEventListener('shopify:section:select', (e) => {
    const dlg = e.target.querySelector && e.target.querySelector('[data-newsletter-popup]');
    if (dlg) {
      initPopup(dlg);
      try {
        dlg.showModal();
      } catch (err) {}
    }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const dlg = e.target.querySelector && e.target.querySelector('[data-newsletter-popup]');
    if (dlg && dlg.open) dlg.close();
  });
})();
